import { computed, Signal, signal, untracked } from '@angular/core'

export interface PreviousActionState<V> {
  readonly state: ActionState
  readonly result: V | null
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  readonly error: unknown | null
}

export interface ActionRequest<T, V> {
  (params: T, previous: PreviousActionState<V>): Promise<V>
}

export type ActionState = 'idle' | 'pending' | 'error' | 'success'

export interface Action<V> {
  readonly result: Signal<V | null>
  readonly error: Signal<unknown>

  readonly isIdle: Signal<boolean>
  readonly isPending: Signal<boolean>

  readonly hasError: Signal<boolean>

  /**
   * whether the state is 'success'. Actually a signal but also narrows the type
   */
  hasResult(): this is Action<V> & { result: Signal<V> }
}

export interface ActionRef<T, V = void> extends Action<V> {
  exec: (params: T) => void
  reset: () => void
  asReadonly: () => Action<V>
}

export function action<T, V = void>(request: ActionRequest<T, V>): ActionRef<T, V> {
  const state = signal<ActionState>('idle')
  const error = signal<unknown>(null)
  const result = signal<V | null>(null)

  function send(params: T) {
    if (untracked(state) === 'pending') {
      throw new Error('Not Allowed - already pending request')
    }
    const previous: PreviousActionState<V> = {
      state: untracked(state),
      result: untracked(result),
      error: untracked(error),
    }
    state.set('pending')

    request(params, previous).then(
      (res: V) => {
        result.set(res)
        state.set('success')
      },
      (err: unknown) => {
        error.set(err)
        state.set('error')
      },
    )
  }

  function reset() {
    if (untracked(state) === 'pending') {
      throw new Error('Not Allowed - request pending')
    }
    state.set('idle')
    error.set(null)
    result.set(null)
  }

  /** the readonly request object */
  const requestObj: Action<V> = {
    isPending: computed<boolean>(() => state() === 'pending'),
    isIdle: computed<boolean>(() => state() === 'idle'),

    result,
    hasResult: <any>computed<boolean>(() => state() === 'success'),

    error,
    hasError: <any>computed<boolean>(() => state() === 'error'),
  }

  /** the RequestRef (writable request object), with the functions to send and reset and to return a readonly version */
  return {
    ...requestObj,
    exec: send,
    reset,
    asReadonly: () => requestObj,
  }
}
